import type { Translator } from '@fd/i18n';
import { backendHeaders, config, deviceHeaders, deviceId } from './config.ts';
import { auth } from './session-auth.ts';

// Calls from the app's own screens (banks, investments, devices, exchange rates) to the sync
// function, one way for all: the session's token, JSON both ways, the server's error code kept.
// Records themselves travel through the sync engine, not through here.

/** The server said no (its error code), or could not be reached (the caller's code for that). */
class ServerError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'ServerError';
    this.code = code;
  }
}

/** The owner's words for a failed call: `<texts>.<code>` when the dictionary has it, else `<texts>.generic`. */
export function serverErrorText(t: Translator, error: unknown, texts: 'banks.error' | 'integrations.error'): string {
  const code = error instanceof ServerError ? error.code : 'generic';
  const text = t.dynamic(`${texts}.${code}`);
  return text === `${texts}.${code}` ? t.dynamic(`${texts}.generic`) : text;
}

export async function callServer<T>(
  path: string,
  options: {
    method?: 'GET' | 'POST' | 'DELETE';
    query?: Record<string, string>;
    body?: unknown;
    /** The code a network failure gets, so the screen can say "the bank is unavailable". */
    unreachable?: string;
    /** Name this device (Settings → Devices lists and marks it). */
    device?: boolean;
  } = {},
): Promise<T> {
  const { method = 'GET', query, body, unreachable = 'UNAVAILABLE', device = false } = options;
  const token = await auth.accessToken();
  const search = query ? `?${new URLSearchParams(query)}` : '';
  let response: Response;
  try {
    response = await fetch(`${config.backendUrl}/functions/v1/sync${path}${search}`, {
      method,
      headers: {
        ...backendHeaders(),
        authorization: `Bearer ${token ?? ''}`,
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(device ? { 'x-device-id': deviceId(), ...deviceHeaders() } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ServerError(unreachable, 'Network error');
  }
  const payload = (await response.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
  if (!response.ok) throw new ServerError(payload?.error?.code ?? 'generic', payload?.error?.message ?? `HTTP ${response.status}`);
  return payload as T;
}
